/**
 * Client ของหน้าจอ RBAC Admin — พูดกับ `/v1/admin/departments` และ
 * `/v1/admin/permissions` ตาม payload contract ที่ freeze ไว้ใน `docs/API_CONTRACT.md`
 *
 * หลักการเดียวกับ `lib/goodsReceipts/client.ts`:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 *   ผู้เรียกจึงเผลอ `catch` แล้วเดาเป็น "สำเร็จ" ไม่ได้
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "ยังไม่มีแผนก" — matrix ว่าง
 *   กับโหลดไม่สำเร็จต้องแยกกันให้ขาด
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 *   (D-049 อนุญาตเฉพาะ session token) — สิทธิ์ที่ cache ไว้คือสิทธิ์ที่อาจถูกถอนไป
 *   แล้ว และ grid ที่แสดงของเก่าจะชวนให้ผู้ดูแลคิดว่าตัวเองเพิ่งถอนสิทธิ์ใครไป
 *
 * `WO-P1-M10-018` เติมส่วนที่เคยเว้นไว้ตอนที่ยังไม่มีหน้าจอของมัน (`employees`,
 * การสร้าง department/employee และการผูกพนักงานเข้ากับแผนก/โรงงาน) — เว้นไว้จนถึง
 * วันที่มีหน้าจอจริง เพราะฟังก์ชันที่เขียนทิ้งไว้ก่อนคือปุ่มที่ไม่มีใครทดสอบ
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5) — ที่นี่สำคัญเป็นพิเศษ เพราะสิ่งที่
 * หน้าจอนี้แก้คือตัวการ์ดเอง
 */

import {
  REDIRECT_URI_KINDS,
  type AppRoleCatalogEntry,
  type AppRoleCatalogPage,
  type AuditSnapshot,
  type CreateOauthClientBody,
  type EmployeeAppRole,
  type EmployeeAppRoles,
  type MfaResetOutcome,
  type OauthClient,
  type OauthClientListPage,
  type OauthClientRedirectUri,
  type OauthClientWithSecret,
  type RedirectUriKind,
  type RevokeEmployeeAppRoleBody,
  type SetEmployeeAppRoleBody,
  type UpdateOauthClientBody,
  type CreateDepartmentBody,
  type CreateEmployeeBody,
  type Department,
  type DepartmentListPage,
  type DepartmentPermissionMatrix,
  type Employee,
  type IssuedLogin,
  type EmployeeDepartmentLink,
  type EmployeeDepartments,
  type EmployeeListPage,
  type EmployeePlantAccess,
  type EmployeePlantGrant,
  type EmployeeSystemRoles,
  type ModulePermissionRow,
  type RbacAuditEntry,
  type RbacAuditLogPage,
  type RoleAssignment,
  type RoleAssignmentListPage,
  type SetEmployeeDepartmentBody,
  type SetEmployeePlantAccessBody,
  type SetModulePermissionBody,
  type SetSystemRoleBody,
  type StoredModulePermission,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/**
 * เส้นทางจริงของกลุ่มนี้ — ตาราง endpoint ใน contract ตัด base prefix ออก
 * แต่ URL ที่ client เรียกขึ้นต้นด้วย `/v1` เสมอ
 */
export const ADMIN_DEPARTMENTS_PATH = '/v1/admin/departments';
export const ADMIN_EMPLOYEES_PATH = '/v1/admin/employees';
export const ADMIN_PERMISSIONS_PATH = '/v1/admin/permissions';
export const ADMIN_ROLES_PATH = '/v1/admin/roles';
export const ADMIN_AUDIT_LOG_PATH = '/v1/admin/audit-log';
export const ADMIN_OAUTH_CLIENTS_PATH = '/v1/admin/oauth-clients';
export const ADMIN_APP_ROLES_PATH = '/v1/admin/app-roles';

/**
 * sub-resource ของพนักงานหนึ่งคน — `employee_id` อยู่ใน **path** ไม่ใช่ใน body
 *
 * `encodeURIComponent` เพราะ `employee_id` เป็น business key ที่ผู้ใช้กำหนดเอง
 * (D-039) ไม่ใช่ UUID ที่ระบบสร้าง — รหัสที่มีอักขระซึ่งมีความหมายใน URL (`/`, `#`,
 * `?`) จะเปลี่ยน**เส้นทางของคำขอ**ทั้งเส้นถ้าไม่ถูก encode ไม่ใช่แค่ทำให้ `404`
 */
export function buildEmployeeDepartmentsPath(employeeId: string): string {
  return `${ADMIN_EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}/departments`;
}

export function buildEmployeeCredentialPath(employeeId: string): string {
  return `${ADMIN_EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}/credential`;
}

export function buildEmployeePasswordResetPath(employeeId: string): string {
  return `${ADMIN_EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}/password-reset`;
}

export function buildEmployeePlantAccessPath(employeeId: string): string {
  return `${ADMIN_EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}/plant-access`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /**
   * `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจออกจากกันโดยไม่เพิ่ม
   * top-level error code ใหม่ `SYSTEM_ADMIN_MODULE_NOT_GRANTABLE` กับ
   * `MODULE_NOT_FOUND` มาพร้อม `422` เหมือนกันทั้งคู่ และต่างกันแค่ค่านี้ค่าเดียว
   * ทั้งที่อันหนึ่งแปลว่า "หน้าจอกับ API ไม่ตรงกัน" ส่วนอีกอันแปลว่า "แคตาล็อกเลื่อน"
   */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ แยกเป็นสถานะของตัวเอง
 *
 * `403` ของกลุ่มนี้แปลว่า **บัญชีนี้ไม่ใช่ ADMIN** ไม่ใช่ "โรงงานนอกขอบเขต"
 * อย่างกลุ่มธุรกิจ — หลัง D-060 มีเพียง system role `ADMIN` ที่ถือ `RBAC_ADMIN` ได้
 * ส่วน `404` แปลว่า **สิ่งที่คำขออ้างถึงไม่มีอยู่**: แผนกนั้นสำหรับ
 * `/admin/permissions` หรือ **พนักงานคนนั้น** สำหรับ `POST /admin/roles` — ทั้งสอง
 * เกิดได้จริง ทั้งจากผู้ดูแลอีกคนที่แก้ข้อมูลระหว่างที่หน้าจอเปิดค้างอยู่ และจาก
 * รหัสพนักงานที่พิมพ์ผิด ผู้เรียกจึงเป็นผู้บอกว่าจะอธิบาย `404` ของตนว่าอะไร
 *
 * `conflict` (`409`) อยู่ในชุดนี้เพราะ **การสร้าง master สองเส้นทาง** เท่านั้น:
 * `POST /admin/departments` (`DEPARTMENT_NAME_TAKEN`) และ `POST /admin/employees`
 * (`EMPLOYEE_ID_TAKEN`) — ทั้งคู่ชนกับ unique constraint ของชื่อ/รหัสที่ผู้ใช้เป็น
 * ผู้ตั้งเอง ไม่ใช่ `409 IDEMPOTENCY_KEY_REUSED`: **ไม่มี endpoint ใดในกลุ่มนี้ใช้
 * `Idempotency-Key`** เพราะการตั้งค่าสิทธิ์/ผูกแผนกไม่เคลื่อน stock หรือบัญชี และ
 * contract ระบุว่า `POST /admin/permissions`, `/admin/roles` และ sub-resource ของ
 * employee ทั้งสองเส้น idempotent อยู่แล้วโดยธรรมชาติ (ทำซ้ำได้ผลเดิม)
 *
 * `409` ต้องเป็นสถานะของตัวเอง **ไม่ใช่ `server-error`**: ชื่อแผนกที่ซ้ำเป็นสิ่งที่
 * ผู้ดูแลแก้เองได้ในสองวินาที ส่วน "เซิร์ฟเวอร์ทำงานไม่สำเร็จ" คือสิ่งที่เขาทำอะไร
 * ไม่ได้เลย — การยุบสองอย่างนี้เข้าด้วยกันคือการส่งเขาไปโทรหาฝ่ายไอที
 */
export type RbacAdminFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type DepartmentListResult =
  | { readonly status: 'ready'; readonly page: DepartmentListPage }
  | RbacAdminFailure;

export type PermissionMatrixResult =
  | { readonly status: 'ready'; readonly matrix: DepartmentPermissionMatrix }
  | RbacAdminFailure;

export type SetModulePermissionResult =
  | { readonly status: 'ready'; readonly stored: StoredModulePermission }
  | RbacAdminFailure;

export type RoleAssignmentListResult =
  | { readonly status: 'ready'; readonly page: RoleAssignmentListPage }
  | RbacAdminFailure;

export type SetSystemRoleResult =
  | { readonly status: 'ready'; readonly roles: EmployeeSystemRoles }
  | RbacAdminFailure;

export type RbacAuditLogResult =
  | { readonly status: 'ready'; readonly page: RbacAuditLogPage }
  | RbacAdminFailure;

export type EmployeeListResult =
  | { readonly status: 'ready'; readonly page: EmployeeListPage }
  | RbacAdminFailure;

export type CreateDepartmentResult =
  | { readonly status: 'ready'; readonly department: Department }
  | RbacAdminFailure;

export type IssueLoginResult =
  | { readonly status: 'ready'; readonly issued: IssuedLogin }
  | RbacAdminFailure;

export type CreateEmployeeResult =
  | { readonly status: 'ready'; readonly employee: Employee }
  | RbacAdminFailure;

export type SetEmployeeDepartmentResult =
  | { readonly status: 'ready'; readonly departments: EmployeeDepartments }
  | RbacAdminFailure;

export type SetEmployeePlantAccessResult =
  | { readonly status: 'ready'; readonly access: EmployeePlantAccess }
  | RbacAdminFailure;

export interface RbacAdminRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ
   *
   * ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับที่ client อื่นใช้ — header
   * `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่ client แต่ละตัว
   */
  readonly fetchImpl?: typeof fetch;
}

/* --------------------------------------------------------------- query ---- */

/**
 * `?department_id=` ของ `GET /v1/admin/permissions`
 *
 * contract ระบุว่าพารามิเตอร์นี้ **บังคับ** (ไม่ส่งตอบ `400`) — matrix เป็นราย
 * department เสมอ ไม่มีมุมมอง "ทุกแผนกรวมกัน"
 */
export function buildPermissionMatrixQuery(departmentId: string): string {
  const params = new URLSearchParams();
  params.append('department_id', departmentId);
  return `?${params.toString()}`;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * ฟิลด์ที่ nullable ได้ — `undefined` แปลว่า "ผิดรูป"
 *
 * แยก "ไม่มีค่า" (null) ออกจาก "ชนิดผิด" (undefined) โดยตั้งใจ
 */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

/**
 * flag ที่ต้องเป็น boolean แท้ ๆ — `null` แปลว่าผิดรูป ไม่ใช่ "ไม่มีสิทธิ์"
 *
 * **ไม่ใช้ `value === true` แบบ deny-safe อย่าง `lib/permissions/client.ts`**
 * โดยตั้งใจ และเหตุผลต่างกันที่ตัวหน้าจอ: ที่นั่นอ่านสิทธิ์ของ *ตัวเอง* เพื่อซ่อนปุ่ม
 * ค่าที่อ่านไม่ออกจึงควรกลายเป็น "ไม่มีสิทธิ์" ส่วนที่นี่คือ **ช่องติ๊กที่ผู้ดูแลจะ
 * บันทึกกลับไป** — ค่าที่อ่านไม่ออกแล้วแสดงเป็นช่องว่าง จะถูกผู้ดูแลกดบันทึกทับเป็น
 * `false` จริง ๆ ทั้งที่เขาไม่ได้ตั้งใจถอนสิทธิ์ใคร payload ที่อ่านไม่ครบจึงต้อง
 * กลายเป็น `invalid-response` แล้วไม่แสดง grid เลย
 */
function requiredBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

/** หนึ่งแผนกจาก list — **บังคับครบทุกฟิลด์ที่ contract บังคับ** */
export function parseDepartment(raw: unknown): Department | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const departmentId = requiredString(record.department_id);
  const departmentName = requiredString(record.department_name);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    departmentId === null ||
    departmentName === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  return {
    department_id: departmentId,
    department_name: departmentName,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/**
 * อ่านรายการแผนกจาก envelope `{ items, next_cursor }` — **และจากที่นั่นเท่านั้น**
 *
 * contract freeze รูปนี้ไว้ตรง ๆ สำหรับกลุ่มนี้ จึงไม่รับ array เปล่าเป็นทางถอย
 * — parser ที่เขียนขึ้นจากรูปที่เดาเอาเองคือ defect ที่ทำให้หน้าจอ BOM เคยขึ้นว่า
 * "ยังไม่มีสูตร" ให้สินค้าที่มีสูตรอยู่แล้ว
 */
export function parseDepartmentListPage(raw: unknown): DepartmentListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: Department[] = [];
  for (const entry of record.items) {
    const parsed = parseDepartment(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  if (cursor === undefined) {
    return null;
  }
  return { items, next_cursor: cursor };
}

/**
 * `next_cursor` ของ envelope — `undefined` แปลว่า "ผิดรูป" ไม่ใช่ "ไม่มีหน้าถัดไป"
 *
 * cursor ที่เป็นสตริงว่างไม่ใช่ "มีหน้าถัดไป" — ปุ่มโหลดเพิ่มที่ส่ง cursor ว่าง
 * จะวนขอหน้าเดิมซ้ำไปเรื่อย ๆ
 */
function parseCursor(raw: unknown): string | null | undefined {
  const cursor = nullableString(raw);
  if (cursor === undefined) {
    return undefined;
  }
  return cursor === null || cursor.trim() === '' ? null : cursor;
}

/**
 * หนึ่งแถวของ matrix — **`is_system_admin_module` บังคับเท่ากับ flag อื่นทุกตัว**
 *
 * ธงนี้คือสิ่งเดียวที่บอกหน้าจอว่าต้องปิดช่องติ๊กแถวไหน (D-060) แถวที่ไม่มีธง
 * หรือมีธงชนิดผิดจึงทำให้ทั้ง payload เป็น `invalid-response` — ไม่ใช่ถูกเดาเป็น
 * `false` ซึ่งจะเปิดช่องให้ติ๊ก `RBAC_ADMIN` ได้ และไม่ใช่ถูกเดาเป็น `true` ซึ่งจะ
 * ปิด module ธุรกิจไปเงียบ ๆ โดยที่ผู้ดูแลไม่รู้ว่าทำไม
 */
export function parseModulePermissionRow(raw: unknown): ModulePermissionRow | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const moduleCode = requiredString(record.module_code);
  const moduleName = requiredString(record.module_name);
  const canCreate = requiredBoolean(record.can_create);
  const canView = requiredBoolean(record.can_view);
  const canEdit = requiredBoolean(record.can_edit);
  const canApprove = requiredBoolean(record.can_approve);
  const isSystemAdminModule = requiredBoolean(record.is_system_admin_module);

  if (
    moduleCode === null ||
    moduleName === null ||
    canCreate === null ||
    canView === null ||
    canEdit === null ||
    canApprove === null ||
    isSystemAdminModule === null
  ) {
    return null;
  }

  return {
    module_code: moduleCode,
    module_name: moduleName,
    can_create: canCreate,
    can_view: canView,
    can_edit: canEdit,
    can_approve: canApprove,
    is_system_admin_module: isSystemAdminModule,
  };
}

/**
 * matrix ของแผนกเดียว — `modules` เป็น **ทั้งแคตาล็อก** ตามที่ contract ระบุ
 *
 * parser ไม่กรองอะไรออกเลย: module ที่ยังไม่เคยถูกตั้งค่าคืน false ทั้งสี่และต้อง
 * ปรากฏใน grid เป็นช่องว่างที่ติ๊กได้ ไม่ใช่หายไป
 */
export function parsePermissionMatrix(raw: unknown): DepartmentPermissionMatrix | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const departmentId = requiredString(record.department_id);
  const departmentName = requiredString(record.department_name);
  if (departmentId === null || departmentName === null || !Array.isArray(record.modules)) {
    return null;
  }

  const modules: ModulePermissionRow[] = [];
  for (const entry of record.modules) {
    const parsed = parseModulePermissionRow(entry);
    if (parsed === null) {
      return null;
    }
    modules.push(parsed);
  }

  return { department_id: departmentId, department_name: departmentName, modules };
}

/** คำตอบของ `POST` — แถวที่เก็บจริง หกคีย์ ไม่มี `module_name`/ธงติดมาด้วย */
export function parseStoredModulePermission(raw: unknown): StoredModulePermission | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const departmentId = requiredString(record.department_id);
  const moduleCode = requiredString(record.module_code);
  const canCreate = requiredBoolean(record.can_create);
  const canView = requiredBoolean(record.can_view);
  const canEdit = requiredBoolean(record.can_edit);
  const canApprove = requiredBoolean(record.can_approve);

  if (
    departmentId === null ||
    moduleCode === null ||
    canCreate === null ||
    canView === null ||
    canEdit === null ||
    canApprove === null
  ) {
    return null;
  }

  return {
    department_id: departmentId,
    module_code: moduleCode,
    can_create: canCreate,
    can_view: canView,
    can_edit: canEdit,
    can_approve: canApprove,
  };
}

/**
 * หนึ่ง role assignment จาก `GET /v1/admin/roles`
 *
 * `role_code` ถูกอ่านเป็น **สตริงอะไรก็ได้ที่ไม่ว่าง** ไม่ใช่ union ของสองค่าที่
 * frontend รู้จัก: contract บอกว่าชุดที่รับได้คือ `SYSTEM_ROLE_CODES` ฝั่ง backend
 * ซึ่งเพิ่มได้ การกรอง role ที่สามทิ้งตรงนี้จะทำให้หน้าจอ**ซ่อนสิทธิ์ที่มีคนถืออยู่จริง**
 * จากผู้ดูแลที่เปิดมาดูว่าใครถืออะไร ซึ่งแย่กว่าการแสดงรหัสที่หน้าจอยังไม่มีคำอธิบายให้
 */
export function parseRoleAssignment(raw: unknown): RoleAssignment | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const employeeId = requiredString(record.employee_id);
  const displayName = requiredString(record.display_name);
  const roleCode = requiredString(record.role_code);
  const createdAt = requiredString(record.created_at);

  if (employeeId === null || displayName === null || roleCode === null || createdAt === null) {
    return null;
  }

  return {
    employee_id: employeeId,
    display_name: displayName,
    role_code: roleCode,
    created_at: createdAt,
  };
}

/** `GET /v1/admin/roles` — envelope `{ items, next_cursor }` เท่านั้น ไม่รับ array เปล่า */
export function parseRoleAssignmentListPage(raw: unknown): RoleAssignmentListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: RoleAssignment[] = [];
  for (const entry of record.items) {
    const parsed = parseRoleAssignment(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

/**
 * คำตอบของ `POST /v1/admin/roles` — สถานะ role ทั้งชุดหลังเปลี่ยน
 *
 * `system_roles` ที่อ่านไม่ออกแม้แต่ตัวเดียวทำให้ทั้งคำตอบผิดรูป ไม่ใช่ถูกตัดแถวนั้น
 * ทิ้งเงียบ ๆ — รายการที่ขาดไปหนึ่ง role คือหน้าจอที่บอกผู้ดูแลว่าเขาเพิ่งถอนสิทธิ์
 * ที่เขาไม่ได้แตะ และเป็นข้อมูลที่เขาจะใช้ตัดสินใจกดครั้งต่อไป
 */
export function parseEmployeeSystemRoles(raw: unknown): EmployeeSystemRoles | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const employeeId = requiredString(record.employee_id);
  if (employeeId === null || !Array.isArray(record.system_roles)) {
    return null;
  }

  const systemRoles: string[] = [];
  for (const entry of record.system_roles) {
    const roleCode = requiredString(entry);
    if (roleCode === null) {
      return null;
    }
    systemRoles.push(roleCode);
  }

  // รายการว่างคือคำตอบที่ถูกต้องและพบบ่อย — เป็นผลลัพธ์ปกติของการถอด role สุดท้าย
  // ของคนคนหนึ่ง (ที่ไม่ใช่ ADMIN คนสุดท้ายของระบบ)
  return { employee_id: employeeId, system_roles: systemRoles };
}

/**
 * `before_json`/`after_json` — object หรือ `null` เท่านั้น
 *
 * `null` คือ **ข้อเท็จจริง** ("ตอนนั้นยังไม่มีสิ่งนี้อยู่") ไม่ใช่ข้อมูลที่หายไป
 * จึงถูกอ่านเป็น `null` ตรง ๆ ส่วนค่าที่เป็นชนิดอื่น (สตริง, ตัวเลข, array) แปลว่า
 * แถวนั้นผิดรูป — snapshot ที่อ่านไม่ออกบนหน้าจอ audit คือหลักฐานที่เชื่อไม่ได้
 */
function nullableSnapshot(value: unknown): AuditSnapshot | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as AuditSnapshot;
}

/**
 * หนึ่งแถวของ `GET /v1/admin/audit-log`
 *
 * ฟิลด์เป้าหมายสี่ตัว (`target_employee_id`, `target_department_id`, `module_code`
 * และ snapshot ทั้งสอง) เป็น `null` ได้ทั้งหมดตามชนิดของการเปลี่ยนแปลง — การบังคับ
 * ให้มีค่าจะทำให้ **การเปลี่ยน role หายไปจากหน้าจอ audit ทั้งหมด** เพราะมันไม่มี
 * `module_code` เลยสักแถว ส่วน `actor_employee_id` กับ `action` บังคับเสมอ: แถว
 * audit ที่ไม่รู้ว่าใครทำหรือทำอะไร ไม่ใช่หลักฐาน
 */
export function parseRbacAuditEntry(raw: unknown): RbacAuditEntry | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const auditId = requiredString(record.audit_id);
  const actorEmployeeId = requiredString(record.actor_employee_id);
  const action = requiredString(record.action);
  const createdAt = requiredString(record.created_at);
  const targetEmployeeId = nullableString(record.target_employee_id);
  const targetDepartmentId = nullableString(record.target_department_id);
  const moduleCode = nullableString(record.module_code);
  const beforeJson = nullableSnapshot(record.before_json);
  const afterJson = nullableSnapshot(record.after_json);

  if (
    auditId === null ||
    actorEmployeeId === null ||
    action === null ||
    createdAt === null ||
    targetEmployeeId === undefined ||
    targetDepartmentId === undefined ||
    moduleCode === undefined ||
    beforeJson === undefined ||
    afterJson === undefined
  ) {
    return null;
  }

  return {
    audit_id: auditId,
    actor_employee_id: actorEmployeeId,
    target_employee_id: targetEmployeeId,
    target_department_id: targetDepartmentId,
    module_code: moduleCode,
    action,
    before_json: beforeJson,
    after_json: afterJson,
    created_at: createdAt,
  };
}

/** `GET /v1/admin/audit-log` — envelope เดิม เรียงใหม่สุดก่อนมาจากเซิร์ฟเวอร์ */
export function parseRbacAuditLogPage(raw: unknown): RbacAuditLogPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: RbacAuditEntry[] = [];
  for (const entry of record.items) {
    const parsed = parseRbacAuditEntry(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

/* ------------------------------------------------ parsing: org resources -- */

/**
 * array ของสตริงที่ไม่ว่าง — `null` แปลว่าผิดรูป, `[]` เป็นคำตอบที่ถูกต้อง
 *
 * ใช้กับ `system_roles` และ `plant_codes` ของ `GET /admin/employees` ซึ่ง contract
 * ระบุว่าว่างได้ทั้งคู่ (พนักงานที่ไม่ถือบทบาทระบบ และพนักงานที่ยังไม่ถูกให้สิทธิ์
 * โรงงานใดเลย) — การตัดสมาชิกที่อ่านไม่ออกทิ้งทีละตัวจะทำให้ **รายการโรงงานที่สั้น
 * ลงหนึ่งอัน** ดูเหมือนการถอนสิทธิ์ที่เกิดขึ้นจริง
 */
function stringArray(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const items: string[] = [];
  for (const entry of value) {
    const parsed = requiredString(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }
  return items;
}

/**
 * หนึ่งพนักงานจาก `GET /v1/admin/employees` (และจาก `POST /v1/admin/employees`
 * ซึ่ง contract ระบุว่าคืนรูปเดียวกัน)
 *
 * `primary_department_id`/`_name` ถูกอ่านเป็น nullable ตรง ๆ เพราะ `null` เป็น
 * **ข้อเท็จจริง** ("คนนี้ไม่สังกัดแผนกใด" — สถานะปกติของ ADMIN/AUDITOR ตาม D-009)
 * ไม่ใช่ข้อมูลที่หายไป ส่วน `competency_flag` บังคับเป็น boolean แท้: มันคือธงที่
 * หน้าจออื่นใช้ตัดสินว่าใครขึ้นเครื่องจักรได้ ค่าที่เดาขึ้นมาไม่ควรเดินทางต่อ
 */
export function parseEmployee(raw: unknown): Employee | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const employeeId = requiredString(record.employee_id);
  const displayName = requiredString(record.display_name);
  const status = requiredString(record.status);
  const competencyFlag = requiredBoolean(record.competency_flag);
  const primaryDepartmentId = nullableString(record.primary_department_id);
  const primaryDepartmentName = nullableString(record.primary_department_name);
  const systemRoles = stringArray(record.system_roles);
  const plantCodes = stringArray(record.plant_codes);
  // เซิร์ฟเวอร์รุ่นเก่าไม่มีฟิลด์ `email` — ไม่มี = ไม่ทราบ ถือเป็น null
  const email = nullableString(record.email);
  // ไม่มีสองคีย์นี้ = เซิร์ฟเวอร์รุ่นก่อน อ่านเป็น "ยังไม่มีบัญชี" แทนการทิ้งทั้งรายการ
  const username = record.username === undefined ? null : nullableString(record.username);
  const loginStatus =
    record.login_status === undefined ? 'NONE' : requiredString(record.login_status);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    employeeId === null ||
    displayName === null ||
    status === null ||
    competencyFlag === null ||
    primaryDepartmentId === undefined ||
    primaryDepartmentName === undefined ||
    systemRoles === null ||
    plantCodes === null ||
    email === undefined ||
    username === undefined ||
    loginStatus === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  return {
    employee_id: employeeId,
    display_name: displayName,
    email,
    status,
    competency_flag: competencyFlag,
    primary_department_id: primaryDepartmentId,
    primary_department_name: primaryDepartmentName,
    system_roles: systemRoles,
    plant_codes: plantCodes,
    username,
    login_status: loginStatus,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/** คำตอบที่มีรหัสชั่วคราว — ของการออกบัญชี (มี username) หรือของการรีเซ็ต (ไม่มี) */
export function parseIssuedLogin(raw: unknown): IssuedLogin | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const employeeId = requiredString(record.employee_id);
  const temporaryPassword = requiredString(record.temporary_password);
  const username = record.username === undefined ? null : requiredString(record.username);
  if (employeeId === null || temporaryPassword === null) {
    return null;
  }
  return { employee_id: employeeId, username, temporary_password: temporaryPassword };
}

/** `GET /v1/admin/employees` — envelope `{ items, next_cursor }` เท่านั้น */
export function parseEmployeeListPage(raw: unknown): EmployeeListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: Employee[] = [];
  for (const entry of record.items) {
    const parsed = parseEmployee(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

/**
 * หนึ่งแถวของ `departments` ในคำตอบของ `POST .../departments`
 *
 * `is_primary` บังคับเป็น boolean แท้ ด้วยเหตุผลเดียวกับ `requiredBoolean` ทั้งไฟล์:
 * ธงที่อ่านไม่ออกแล้วถูกเดาเป็น `false` จะทำให้หน้าจอบอกว่า "ไม่มีแผนกหลัก" ทั้งที่
 * เพิ่งตั้งสำเร็จ และผู้ดูแลจะกดตั้งซ้ำอีกครั้ง
 */
export function parseEmployeeDepartmentLink(raw: unknown): EmployeeDepartmentLink | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const departmentId = requiredString(record.department_id);
  const departmentName = requiredString(record.department_name);
  const isPrimary = requiredBoolean(record.is_primary);

  if (departmentId === null || departmentName === null || isPrimary === null) {
    return null;
  }
  return {
    department_id: departmentId,
    department_name: departmentName,
    is_primary: isPrimary,
  };
}

/**
 * คำตอบของ `POST /v1/admin/employees/{id}/departments` — **สถานะทั้งชุดหลังเปลี่ยน**
 *
 * แถวเดียวที่อ่านไม่ออกทำให้ทั้งคำตอบผิดรูป ไม่ใช่ถูกตัดทิ้งเงียบ ๆ: ชุดนี้คือสิ่ง
 * เดียวที่หน้าจอใช้แสดงว่าแผนกไหนเป็นแผนกหลัก และชุดที่ขาดไปหนึ่งแถวคือหน้าจอที่บอก
 * ผู้ดูแลว่าเขาเพิ่งถอนสังกัดที่เขาไม่ได้แตะ
 *
 * **รายการว่างเป็นคำตอบที่ถูกต้อง** และเป็นผลลัพธ์ปกติของการถอนสังกัดสุดท้าย —
 * ADMIN/AUDITOR ไม่จำเป็นต้องมีแผนกเลย (D-009)
 */
export function parseEmployeeDepartments(raw: unknown): EmployeeDepartments | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const employeeId = requiredString(record.employee_id);
  if (employeeId === null || !Array.isArray(record.departments)) {
    return null;
  }

  const departments: EmployeeDepartmentLink[] = [];
  for (const entry of record.departments) {
    const parsed = parseEmployeeDepartmentLink(entry);
    if (parsed === null) {
      return null;
    }
    departments.push(parsed);
  }

  return { employee_id: employeeId, departments };
}

/** หนึ่งโรงงานที่พนักงานเข้าถึงได้ — `plant_code` เป็น business code ไม่ใช่ UUID */
export function parseEmployeePlantGrant(raw: unknown): EmployeePlantGrant | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const plantCode = requiredString(record.plant_code);
  const isHomePlant = requiredBoolean(record.is_home_plant);

  if (plantCode === null || isHomePlant === null) {
    return null;
  }
  return { plant_code: plantCode, is_home_plant: isHomePlant };
}

/**
 * คำตอบของ `POST /v1/admin/employees/{id}/plant-access` — สถานะทั้งชุดหลังเปลี่ยน
 *
 * เข้มเท่ากับ `parseEmployeeDepartments` และด้วยเหตุผลที่หนักกว่า: ชุดนี้คือ
 * **plant scope ของพนักงาน (D-004)** ซึ่งตัดสินว่าเขาเห็นข้อมูลของโรงงานใดได้บ้าง
 */
export function parseEmployeePlantAccess(raw: unknown): EmployeePlantAccess | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const employeeId = requiredString(record.employee_id);
  if (employeeId === null || !Array.isArray(record.plant_access)) {
    return null;
  }

  const grants: EmployeePlantGrant[] = [];
  for (const entry of record.plant_access) {
    const parsed = parseEmployeePlantGrant(entry);
    if (parsed === null) {
      return null;
    }
    grants.push(parsed);
  }

  return { employee_id: employeeId, plant_access: grants };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | RbacAdminFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: RbacAdminRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/** `GET /v1/admin/departments` — master list ขององค์กร เรียงตามชื่อแผนก */
export async function fetchDepartments(
  options: RbacAdminRequestOptions = {},
): Promise<DepartmentListResult> {
  const result = await requestPayload(
    ADMIN_DEPARTMENTS_PATH,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseDepartmentListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** `GET /v1/admin/employees` — ทุกคนในองค์กร พร้อมแผนกหลัก บทบาท และโรงงาน */
export async function fetchEmployees(
  options: RbacAdminRequestOptions = {},
): Promise<EmployeeListResult> {
  const result = await requestPayload(
    ADMIN_EMPLOYEES_PATH,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseEmployeeListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * แปลง body ของ `POST /v1/admin/departments` ให้เหลือคีย์เดียว (`extra = "forbid"`)
 *
 * ด่านเดียวกับ `serializeSetModulePermissionBody`: object ที่ถูก spread มาทั้งก้อน
 * (เช่นแถวของแผนกที่มี `department_id` ติดมาด้วย) จะถูกตอบ `400` ทั้งที่ผู้ดูแล
 * พิมพ์ชื่อถูก
 */
export function serializeCreateDepartmentBody(
  body: CreateDepartmentBody,
): Record<string, unknown> {
  return { department_name: body.department_name };
}

/**
 * `POST /v1/admin/departments` — สร้างแผนกใหม่ ตอบ `201`
 *
 * แผนกที่เพิ่งสร้าง **ยังไม่มีสิทธิ์อะไรเลย** (deny-by-default) จนกว่าจะมีคนติ๊ก
 * ช่องให้ที่หน้าตารางสิทธิ์ — เป็นคนละคำขอและคนละหน้าจอ
 */
export async function createDepartment(
  body: CreateDepartmentBody,
  options: RbacAdminRequestOptions = {},
): Promise<CreateDepartmentResult> {
  const result = await requestPayload(
    ADMIN_DEPARTMENTS_PATH,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeCreateDepartmentBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const department = parseDepartment(result.payload);
  return department === null
    ? { status: 'invalid-response' }
    : { status: 'ready', department };
}

/**
 * แปลง body ของ `POST /v1/admin/employees` — สองคีย์บังคับ + `status` เมื่อระบุ
 *
 * `status` ถูก **ละทิ้งไปเลยเมื่อไม่ได้ระบุ** ไม่ใช่ส่ง `null` หรือสตริงว่าง:
 * contract บอกว่ามันไม่บังคับและเซิร์ฟเวอร์เติม `ACTIVE` ให้ ส่วน `null` กับสตริง
 * ว่างเป็นค่าที่ `extra = "forbid"` + enum จะปฏิเสธด้วย `400`
 */
export function serializeCreateEmployeeBody(
  body: CreateEmployeeBody,
): Record<string, unknown> {
  return {
    employee_id: body.employee_id,
    display_name: body.display_name,
    ...(body.status === undefined ? {} : { status: body.status }),
  };
}

/**
 * `POST /v1/admin/employees` — สร้าง employee master ตอบ `201`
 *
 * ⚠️ **ไม่สร้าง credential** คนที่เพิ่งถูกสร้างยัง **เข้าสู่ระบบไม่ได้** จนกว่าจะมี
 * การออก username/รหัสผ่านให้ผ่านเส้นทางของมันเอง (`employee_credential` เป็นคนละ
 * ตารางและคนละคำขอ) — หน้าจอต้องพูดเรื่องนี้ออกมา ไม่ใช่ปล่อยให้ผู้ดูแลไปค้นพบเอง
 * ตอนที่พนักงานคนนั้นโทรมาบอกว่าล็อกอินไม่ได้
 */
export async function createEmployee(
  body: CreateEmployeeBody,
  options: RbacAdminRequestOptions = {},
): Promise<CreateEmployeeResult> {
  const result = await requestPayload(
    ADMIN_EMPLOYEES_PATH,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeCreateEmployeeBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const employee = parseEmployee(result.payload);
  return employee === null ? { status: 'invalid-response' } : { status: 'ready', employee };
}

/** แปลง body ของ `POST .../departments` — สามคีย์ ไม่มีคีย์ที่สี่ */
export function serializeSetEmployeeDepartmentBody(
  body: SetEmployeeDepartmentBody,
): Record<string, unknown> {
  return {
    department_id: body.department_id,
    is_primary: body.is_primary,
    granted: body.granted,
  };
}

/**
 * `POST /v1/admin/employees/{employee_id}/departments` — ผูกหรือถอนหนึ่งแผนก
 *
 * ⚠️ **`is_primary: true` คือการ "ย้าย" ไม่ใช่ "เพิ่ม"** เซิร์ฟเวอร์ปลดธงของแผนกอื่น
 * ของคนเดียวกันในธุรกรรมเดียว ผู้เรียกจึงต้องอ่านสถานะใหม่จาก **คำตอบ** ไม่ใช่จาก
 * สิ่งที่ตัวเองเพิ่งส่งไป — ฟังก์ชันนี้คืนชุดทั้งหมดหลังเปลี่ยนให้ด้วยเหตุผลนั้น
 */
export async function setEmployeeDepartment(
  employeeId: string,
  body: SetEmployeeDepartmentBody,
  options: RbacAdminRequestOptions = {},
): Promise<SetEmployeeDepartmentResult> {
  const result = await requestPayload(
    buildEmployeeDepartmentsPath(employeeId),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeSetEmployeeDepartmentBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const departments = parseEmployeeDepartments(result.payload);
  return departments === null
    ? { status: 'invalid-response' }
    : { status: 'ready', departments };
}

/** แปลง body ของ `POST .../plant-access` — สามคีย์ ไม่มีคีย์ที่สี่ */
export function serializeSetEmployeePlantAccessBody(
  body: SetEmployeePlantAccessBody,
): Record<string, unknown> {
  return {
    plant_code: body.plant_code,
    is_home_plant: body.is_home_plant,
    granted: body.granted,
  };
}

/**
 * `POST /v1/admin/employees/{employee_id}/plant-access` — ให้/ถอนสิทธิ์หนึ่งโรงงาน
 *
 * `is_home_plant` เป็น singleton แบบเดียวกับ `is_primary` และคำตอบคือ **plant scope
 * ทั้งชุดหลังเปลี่ยน** (D-004) ซึ่งมีผลทันทีที่คำขอนี้ตอบกลับ
 */
export async function setEmployeePlantAccess(
  employeeId: string,
  body: SetEmployeePlantAccessBody,
  options: RbacAdminRequestOptions = {},
): Promise<SetEmployeePlantAccessResult> {
  const result = await requestPayload(
    buildEmployeePlantAccessPath(employeeId),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeSetEmployeePlantAccessBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const access = parseEmployeePlantAccess(result.payload);
  return access === null ? { status: 'invalid-response' } : { status: 'ready', access };
}

/** `GET /v1/admin/permissions?department_id=` — matrix ทั้งแคตาล็อกของแผนกนั้น */
export async function fetchPermissionMatrix(
  departmentId: string,
  options: RbacAdminRequestOptions = {},
): Promise<PermissionMatrixResult> {
  const result = await requestPayload(
    `${ADMIN_PERMISSIONS_PATH}${buildPermissionMatrixQuery(departmentId)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const matrix = parsePermissionMatrix(result.payload);
  return matrix === null ? { status: 'invalid-response' } : { status: 'ready', matrix };
}

/**
 * แปลง body ให้เหลือเฉพาะหกคีย์ที่ contract รับ — และไม่มีคีย์ที่เจ็ด
 *
 * ด่านที่สองต่อจากชนิดข้อมูล: ต่อให้ object ที่ส่งเข้ามามีคีย์แปลกปลอมติดมา (เช่น
 * แถวของ matrix ที่ถูก spread ทั้งก้อน) สิ่งที่ออกไปกับ request ยังเป็นเฉพาะคีย์ที่นี่
 * — `module_name` และ **`is_system_admin_module` จึงไปถึงเซิร์ฟเวอร์ไม่ได้เลย** ซึ่ง
 * สำคัญเพราะ `extra = "forbid"`: แถวที่ spread มาทั้งก้อนจะถูกตอบ `422` ทั้งที่ผู้ดูแล
 * ติ๊กถูกทุกช่อง
 *
 * flag ทั้งสี่เขียนลงไปเป็น boolean ตรง ๆ ไม่ผ่านการแปลงใด — `can_edit` ไม่เคย
 * ลาก `can_approve` ตามไปด้วย (segregation of duties เป็นกฎรายเอกสารที่ service
 * บังคับ ไม่ใช่ข้อห้ามที่ matrix)
 */
export function serializeSetModulePermissionBody(
  body: SetModulePermissionBody,
): Record<string, unknown> {
  return {
    department_id: body.department_id,
    module_code: body.module_code,
    can_create: body.can_create,
    can_view: body.can_view,
    can_edit: body.can_edit,
    can_approve: body.can_approve,
  };
}

/**
 * `POST /v1/admin/permissions` — upsert แถวเดียวของ (department, module)
 *
 * **ไม่มี `Idempotency-Key`** โดยเจตนาและตาม contract: การตั้งค่าสิทธิ์ไม่ใช่ side
 * effect ที่เคลื่อน stock/บัญชี (ต่างจาก scan/receipt/import/sync) และการตั้ง flag
 * เดิมซ้ำได้ผลเดิมอยู่แล้วโดยธรรมชาติ การเติมคีย์ที่ endpoint ไม่ได้ขอเข้าไปคือ
 * header ที่ไม่มีใครอ่าน ซึ่งจะสอนคนอ่านโค้ดผิดว่า endpoint นี้ต้องการมัน
 *
 * ⚠️ ฟังก์ชันนี้ **ไม่ได้ตรวจ `is_system_admin_module` ให้** — มันรับแค่หกคีย์ที่
 * contract รับ และไม่มีทางรู้จักธงนั้น การกันตาม D-060 อยู่ที่หน้าจอ (ช่องติ๊กถูก
 * disable และไม่มีเส้นทางประกอบ `POST`) และที่ backend (`422`) ซึ่งเป็นด่านจริง
 */
export async function setModulePermission(
  body: SetModulePermissionBody,
  options: RbacAdminRequestOptions = {},
): Promise<SetModulePermissionResult> {
  const result = await requestPayload(
    ADMIN_PERMISSIONS_PATH,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeSetModulePermissionBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const stored = parseStoredModulePermission(result.payload);
  return stored === null ? { status: 'invalid-response' } : { status: 'ready', stored };
}

/**
 * `GET /v1/admin/roles` — role assignment ทุกแถวขององค์กร
 *
 * ไม่มีพารามิเตอร์ตัวกรองเลย ตามที่ contract เขียนไว้: `ADMIN`/`AUDITOR` เป็นสอง
 * บทบาทพิเศษที่มีคนถืออยู่ไม่กี่คน ทั้งรายการจึงเป็นหน้าเดียวและ `next_cursor`
 * เป็น `null` เสมอ
 */
export async function fetchRoleAssignments(
  options: RbacAdminRequestOptions = {},
): Promise<RoleAssignmentListResult> {
  const result = await requestPayload(
    ADMIN_ROLES_PATH,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseRoleAssignmentListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * แปลง body ให้เหลือเฉพาะสามคีย์ที่ contract รับ — และไม่มีคีย์ที่สี่
 *
 * ด่านเดียวกับ `serializeSetModulePermissionBody`: `extra = "forbid"` ทำให้คีย์
 * แปลกปลอมที่ติดมา (เช่น `display_name` จากแถวที่ถูก spread ทั้งก้อน) กลายเป็น
 * `400` ทั้งที่ผู้ดูแลกดถูกทุกอย่าง
 */
export function serializeSetSystemRoleBody(
  body: SetSystemRoleBody,
): Record<string, unknown> {
  return {
    employee_id: body.employee_id,
    role_code: body.role_code,
    granted: body.granted,
  };
}

/**
 * `POST /v1/admin/roles` — มอบ (`granted: true`) หรือถอด (`granted: false`) หนึ่ง role
 *
 * **ไม่มี `Idempotency-Key`** ตาม contract ด้วยเหตุผลเดียวกับ
 * `setModulePermission`: มอบสิ่งที่ถืออยู่แล้ว/ถอนสิ่งที่ไม่ได้ถือ ไม่ทำอะไรและได้
 * ผลลัพธ์เดิม การมอบ role ไม่ได้เคลื่อนสต๊อกหรือบัญชี
 *
 * ⚠️ ฟังก์ชันนี้ **ไม่ได้ตรวจว่า `role_code` อยู่ในชุดที่รับได้** และไม่ได้รู้จัก
 * การ์ดกัน lockout เลย — ทั้งสองอย่างเป็นคำตอบของเซิร์ฟเวอร์ (`422` พร้อม
 * `details.rule`) ซึ่งเป็นด่านจริง หน้าที่ของที่นี่คือไม่กลืนมัน
 */
export async function setSystemRole(
  body: SetSystemRoleBody,
  options: RbacAdminRequestOptions = {},
): Promise<SetSystemRoleResult> {
  const result = await requestPayload(
    ADMIN_ROLES_PATH,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeSetSystemRoleBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const roles = parseEmployeeSystemRoles(result.payload);
  return roles === null ? { status: 'invalid-response' } : { status: 'ready', roles };
}

/**
 * `GET /v1/admin/audit-log` — ร่องรอยการเปลี่ยนสิทธิ์ ใหม่สุดก่อน
 *
 * **ไม่ส่งตัวกรองใดไปเลย** ทั้งที่ contract รองรับสามตัว (`target_employee_id`,
 * `target_department_id`, `action`): หน้าจอของ WO นี้อ่านทั้งสมุด และตัวกรองที่
 * client ส่งได้แต่ไม่มีหน้าจอไหนใช้คือพารามิเตอร์ที่ไม่มีใครทดสอบ — เพิ่มพร้อมกับ
 * ตัวกรองบนหน้าจอวันที่มีคนขอ
 */
export async function fetchRbacAuditLog(
  options: RbacAdminRequestOptions = {},
): Promise<RbacAuditLogResult> {
  const result = await requestPayload(
    ADMIN_AUDIT_LOG_PATH,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseRbacAuditLogPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี
 *
 * ไม่เดาค่าเมื่ออ่านไม่ได้ เพราะกฎที่เดาผิดจะส่งผู้ใช้ไปแก้เรื่องที่ไม่ได้เสีย
 */
export function ruleFromFailure(failure: RbacAdminFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}

/* ------------------------------------------------------- SSO admin (D-153) -- */

export function buildOauthClientPath(clientId: string): string {
  return `${ADMIN_OAUTH_CLIENTS_PATH}/${encodeURIComponent(clientId)}`;
}

export function buildRotateSecretPath(clientId: string): string {
  return `${buildOauthClientPath(clientId)}/rotate-secret`;
}

export function buildEmployeePath(employeeId: string): string {
  return `${ADMIN_EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}`;
}

export function buildEmployeeAppRolesPath(employeeId: string): string {
  return `${buildEmployeePath(employeeId)}/app-roles`;
}

export function buildEmployeeAppRolePath(employeeId: string, appCode: string): string {
  return `${buildEmployeeAppRolesPath(employeeId)}/${encodeURIComponent(appCode)}`;
}

export function buildEmployeeAppRolesRevokePath(employeeId: string): string {
  return `${buildEmployeeAppRolesPath(employeeId)}/revoke`;
}

export function buildEmployeeMfaResetPath(employeeId: string): string {
  return `${buildEmployeePath(employeeId)}/mfa/reset`;
}

export type OauthClientListResult =
  | { readonly status: 'ready'; readonly page: OauthClientListPage }
  | RbacAdminFailure;

export type OauthClientResult =
  | { readonly status: 'ready'; readonly client: OauthClient }
  | RbacAdminFailure;

/** `client_secret` อยู่ในผลนี้ **ที่เดียว** — ผู้เรียกต้องไม่เก็บลง storage/log */
export type OauthClientSecretResult =
  | { readonly status: 'ready'; readonly issued: OauthClientWithSecret }
  | RbacAdminFailure;

export type AppRoleCatalogResult =
  | { readonly status: 'ready'; readonly page: AppRoleCatalogPage }
  | RbacAdminFailure;

export type EmployeeAppRolesResult =
  | { readonly status: 'ready'; readonly roles: EmployeeAppRoles }
  | RbacAdminFailure;

/** การเขียนที่ไม่ต้องอ่านคำตอบ — ผู้เรียกโหลดสถานะจริงซ้ำเอง */
export type AckResult = { readonly status: 'done' } | RbacAdminFailure;

export type MfaResetResult =
  | { readonly status: 'done'; readonly outcome: MfaResetOutcome | null }
  | RbacAdminFailure;

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw);
}

function nonNegativeInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

/** `null` = ผิดรูป; ไม่มีค่าในเซิร์ฟเวอร์ = `{ value: null }` */
function nullableInteger(value: unknown): { readonly value: number | null } | null {
  if (value === null || value === undefined) {
    return { value: null };
  }
  const parsed = nonNegativeInteger(value);
  return parsed === null ? null : { value: parsed };
}

export function parseRedirectUri(raw: unknown): OauthClientRedirectUri | null {
  if (!isRecord(raw)) {
    return null;
  }
  const uri = requiredString(raw.uri);
  const kind = raw.kind;
  if (uri === null || !REDIRECT_URI_KINDS.includes(kind as RedirectUriKind)) {
    return null;
  }
  return { uri, kind: kind as RedirectUriKind };
}

export function parseOauthClient(raw: unknown): OauthClient | null {
  if (!isRecord(raw)) {
    return null;
  }
  const clientId = requiredString(raw.client_id);
  const appCode = requiredString(raw.app_code);
  const clientName = requiredString(raw.client_name);
  const active = requiredBoolean(raw.active);
  const requireAppRole = requiredBoolean(raw.require_app_role);
  const refreshEnabled = requiredBoolean(raw.refresh_enabled);
  const accessTtl = nonNegativeInteger(raw.access_token_ttl_seconds);
  const idTtl = nonNegativeInteger(raw.id_token_ttl_seconds);
  const absoluteTtl = nullableInteger(raw.refresh_token_absolute_ttl_seconds);
  const idleTtl = nullableInteger(raw.refresh_token_idle_ttl_seconds);
  const mfaRoles = stringArray(raw.mfa_roles);
  const rotatedAt = nullableString(raw.secret_rotated_at);

  if (
    clientId === null ||
    appCode === null ||
    clientName === null ||
    active === null ||
    requireAppRole === null ||
    refreshEnabled === null ||
    accessTtl === null ||
    idTtl === null ||
    absoluteTtl === null ||
    idleTtl === null ||
    mfaRoles === null ||
    rotatedAt === undefined ||
    !Array.isArray(raw.redirect_uris)
  ) {
    return null;
  }

  const redirectUris: OauthClientRedirectUri[] = [];
  for (const entry of raw.redirect_uris) {
    const parsed = parseRedirectUri(entry);
    if (parsed === null) {
      return null;
    }
    redirectUris.push(parsed);
  }

  return {
    client_id: clientId,
    app_code: appCode,
    client_name: clientName,
    active,
    require_app_role: requireAppRole,
    access_token_ttl_seconds: accessTtl,
    id_token_ttl_seconds: idTtl,
    refresh_enabled: refreshEnabled,
    refresh_token_absolute_ttl_seconds: absoluteTtl.value,
    refresh_token_idle_ttl_seconds: idleTtl.value,
    redirect_uris: redirectUris,
    mfa_roles: mfaRoles,
    secret_rotated_at: rotatedAt,
  };
}

export function parseOauthClientListPage(raw: unknown): OauthClientListPage | null {
  if (!isRecord(raw) || !Array.isArray(raw.items)) {
    return null;
  }
  const items: OauthClient[] = [];
  for (const entry of raw.items) {
    const parsed = parseOauthClient(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }
  const cursor = parseCursor(raw.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

/**
 * คำตอบของสร้าง/หมุน secret — รับทั้งรูปแบน (ฟิลด์ client + `client_secret`) และรูปซ้อน
 * (`{ client, client_secret }`) เพราะ contract ยังไม่ระบุ; secret ว่างหรือไม่มี = ผิดรูป
 * (ไม่แสดง modal ที่ว่างเปล่า)
 */
export function parseOauthClientWithSecret(raw: unknown): OauthClientWithSecret | null {
  if (!isRecord(raw)) {
    return null;
  }
  // the backend's one shape: { client, client_secret } (WO-P2-M13-005)
  const secret = requiredString(raw.client_secret);
  const client = isRecord(raw.client) ? parseOauthClient(raw.client) : null;
  if (secret === null || client === null) {
    return null;
  }
  return { client, client_secret: secret };
}

export function parseAppRoleCatalogEntry(raw: unknown): AppRoleCatalogEntry | null {
  if (!isRecord(raw)) {
    return null;
  }
  const appCode = requiredString(raw.app_code);
  const appName = nullableString(raw.app_name);
  const roleCode = requiredString(raw.role_code);
  const description = typeof raw.description === 'string' ? raw.description : '';
  const active = raw.active === undefined ? true : requiredBoolean(raw.active);
  const appActive = raw.app_active === undefined ? true : requiredBoolean(raw.app_active);
  if (appCode === null || appName === undefined || roleCode === null || active === null || appActive === null) {
    return null;
  }
  return { app_code: appCode, app_name: appName, app_active: appActive, role_code: roleCode, description, active };
}

export function parseAppRoleCatalogPage(raw: unknown): AppRoleCatalogPage | null {
  if (!isRecord(raw) || !Array.isArray(raw.items)) {
    return null;
  }
  const items: AppRoleCatalogEntry[] = [];
  for (const entry of raw.items) {
    const parsed = parseAppRoleCatalogEntry(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }
  const cursor = parseCursor(raw.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

export function parseEmployeeAppRole(raw: unknown): EmployeeAppRole | null {
  if (!isRecord(raw)) {
    return null;
  }
  const appCode = requiredString(raw.app_code);
  const roleCode = requiredString(raw.role_code);
  const grantedBy = nullableString(raw.granted_by_employee_id);
  const grantedAt = nullableString(raw.granted_at);
  const selfGranted = requiredBoolean(raw.self_granted);
  if (
    appCode === null ||
    roleCode === null ||
    grantedBy === undefined ||
    grantedAt === undefined ||
    selfGranted === null
  ) {
    return null;
  }
  return {
    app_code: appCode,
    role_code: roleCode,
    granted_by_employee_id: grantedBy,
    granted_at: grantedAt,
    self_granted: selfGranted,
  };
}

export function parseEmployeeAppRoles(raw: unknown): EmployeeAppRoles | null {
  if (!isRecord(raw)) {
    return null;
  }
  const employeeId = requiredString(raw.employee_id);
  if (employeeId === null || !Array.isArray(raw.app_roles)) {
    return null;
  }
  const roles: EmployeeAppRole[] = [];
  for (const entry of raw.app_roles) {
    const parsed = parseEmployeeAppRole(entry);
    if (parsed === null) {
      return null;
    }
    roles.push(parsed);
  }
  return { employee_id: employeeId, app_roles: roles };
}

function parseMfaResetOutcome(raw: unknown): MfaResetOutcome | null {
  if (!isRecord(raw)) {
    return null;
  }
  const employeeId = requiredString(raw.employee_id);
  const status = requiredString(raw.status);
  const sessions = nonNegativeInteger(raw.revoked_session_count);
  const idp = nonNegativeInteger(raw.revoked_idp_session_count);
  const grants = nonNegativeInteger(raw.revoked_grant_count);
  if (
    employeeId === null ||
    status === null ||
    sessions === null ||
    idp === null ||
    grants === null
  ) {
    return null;
  }
  return {
    employee_id: employeeId,
    status,
    revoked_session_count: sessions,
    revoked_idp_session_count: idp,
    revoked_grant_count: grants,
  };
}

const JSON_HEADERS = { Accept: 'application/json', 'Content-Type': 'application/json' };

function serializeRedirectUris(
  uris: readonly OauthClientRedirectUri[],
): Record<string, unknown>[] {
  return uris.map((entry) => ({ uri: entry.uri, kind: entry.kind }));
}

export function serializeCreateOauthClientBody(
  body: CreateOauthClientBody,
): Record<string, unknown> {
  return {
    client_id: body.client_id,
    app_code: body.app_code,
    client_name: body.client_name,
    redirect_uris: serializeRedirectUris(body.redirect_uris),
    mfa_roles: [...body.mfa_roles],
    access_token_ttl_seconds: body.access_token_ttl_seconds,
    id_token_ttl_seconds: body.id_token_ttl_seconds,
    refresh_enabled: body.refresh_enabled,
    refresh_token_absolute_ttl_seconds: body.refresh_token_absolute_ttl_seconds,
    refresh_token_idle_ttl_seconds: body.refresh_token_idle_ttl_seconds,
    require_app_role: body.require_app_role,
  };
}

/**
 * PATCH body. With `original`, only the fields that differ from what the form was opened with are sent, so saving
 * does not undo another admin's concurrent change to a field this admin did not touch (e.g. `active`).
 */
export function serializeUpdateOauthClientBody(
  body: UpdateOauthClientBody,
  original?: OauthClient,
): Record<string, unknown> {
  const full = fullUpdateBody(body);
  if (original === undefined) {
    return full;
  }
  const before = fullUpdateBody({
    redirect_uris: original.redirect_uris,
    mfa_roles: original.mfa_roles,
    access_token_ttl_seconds: original.access_token_ttl_seconds,
    id_token_ttl_seconds: original.id_token_ttl_seconds,
    refresh_enabled: original.refresh_enabled,
    refresh_token_absolute_ttl_seconds: original.refresh_token_absolute_ttl_seconds,
    refresh_token_idle_ttl_seconds: original.refresh_token_idle_ttl_seconds,
    require_app_role: original.require_app_role,
    active: original.active,
  });
  return Object.fromEntries(
    Object.entries(full).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(before[key])),
  );
}

function fullUpdateBody(body: UpdateOauthClientBody): Record<string, unknown> {
  return {
    redirect_uris: serializeRedirectUris(body.redirect_uris),
    mfa_roles: [...body.mfa_roles],
    access_token_ttl_seconds: body.access_token_ttl_seconds,
    id_token_ttl_seconds: body.id_token_ttl_seconds,
    refresh_enabled: body.refresh_enabled,
    refresh_token_absolute_ttl_seconds: body.refresh_token_absolute_ttl_seconds,
    refresh_token_idle_ttl_seconds: body.refresh_token_idle_ttl_seconds,
    require_app_role: body.require_app_role,
    active: body.active,
  };
}

export async function fetchOauthClients(
  options: RbacAdminRequestOptions = {},
): Promise<OauthClientListResult> {
  const result = await requestPayload(
    ADMIN_OAUTH_CLIENTS_PATH,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const page = parseOauthClientListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

export async function createOauthClient(
  body: CreateOauthClientBody,
  options: RbacAdminRequestOptions = {},
): Promise<OauthClientSecretResult> {
  const result = await requestPayload(
    ADMIN_OAUTH_CLIENTS_PATH,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(serializeCreateOauthClientBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const issued = parseOauthClientWithSecret(result.payload);
  return issued === null ? { status: 'invalid-response' } : { status: 'ready', issued };
}

export async function updateOauthClient(
  clientId: string,
  body: UpdateOauthClientBody,
  options: RbacAdminRequestOptions = {},
  original?: OauthClient,
): Promise<OauthClientResult> {
  const result = await requestPayload(
    buildOauthClientPath(clientId),
    {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify(serializeUpdateOauthClientBody(body, original)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const client = parseOauthClient(result.payload);
  return client === null ? { status: 'invalid-response' } : { status: 'ready', client };
}

export async function rotateOauthClientSecret(
  clientId: string,
  options: RbacAdminRequestOptions = {},
): Promise<OauthClientSecretResult> {
  const result = await requestPayload(
    buildRotateSecretPath(clientId),
    { method: 'POST', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const issued = parseOauthClientWithSecret(result.payload);
  return issued === null ? { status: 'invalid-response' } : { status: 'ready', issued };
}

export async function fetchAppRoleCatalog(
  options: RbacAdminRequestOptions = {},
): Promise<AppRoleCatalogResult> {
  const result = await requestPayload(
    ADMIN_APP_ROLES_PATH,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const page = parseAppRoleCatalogPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

export async function fetchEmployeeAppRoles(
  employeeId: string,
  options: RbacAdminRequestOptions = {},
): Promise<EmployeeAppRolesResult> {
  const result = await requestPayload(
    buildEmployeeAppRolesPath(employeeId),
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const roles = parseEmployeeAppRoles(result.payload);
  return roles === null ? { status: 'invalid-response' } : { status: 'ready', roles };
}

/**
 * สำเร็จ = จบ — `invalid-response` จาก `requestPayload` เกิดได้เฉพาะตอน 2xx ที่ไม่มี JSON
 * body (เช่น 204) จึงนับเป็นสำเร็จ ส่วนสถานะจริงให้ผู้เรียกโหลดซ้ำเอง
 */
function ack(result: PayloadResult): AckResult {
  if (result.status === 'payload' || result.status === 'invalid-response') {
    return { status: 'done' };
  }
  return result;
}

export async function setEmployeeAppRole(
  employeeId: string,
  appCode: string,
  body: SetEmployeeAppRoleBody,
  options: RbacAdminRequestOptions = {},
): Promise<AckResult> {
  return ack(
    await requestPayload(
      buildEmployeeAppRolePath(employeeId, appCode),
      { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ role_code: body.role_code }) },
      options,
    ),
  );
}

export async function revokeEmployeeAppRole(
  employeeId: string,
  body: RevokeEmployeeAppRoleBody,
  options: RbacAdminRequestOptions = {},
): Promise<AckResult> {
  return ack(
    await requestPayload(
      buildEmployeeAppRolesRevokePath(employeeId),
      { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ app_code: body.app_code }) },
      options,
    ),
  );
}

export async function resetEmployeeMfa(
  employeeId: string,
  options: RbacAdminRequestOptions = {},
): Promise<MfaResetResult> {
  const result = await requestPayload(
    buildEmployeeMfaResetPath(employeeId),
    { method: 'POST', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status === 'payload') {
    return { status: 'done', outcome: parseMfaResetOutcome(result.payload) };
  }
  if (result.status === 'invalid-response') {
    return { status: 'done', outcome: null };
  }
  return result;
}

/** `POST /v1/admin/employees/{employee_id}/credential` — ส่งแค่ username; รหัสชั่วคราวเซิร์ฟเวอร์สุ่มและต้องเปลี่ยนตอนล็อกอินครั้งแรก */
export async function issueEmployeeCredential(
  employeeId: string,
  username: string,
  options: RbacAdminRequestOptions = {},
): Promise<IssueLoginResult> {
  const result = await requestPayload(
    buildEmployeeCredentialPath(employeeId),
    { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ username }) },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const issued = parseIssuedLogin(result.payload);
  return issued === null ? { status: 'invalid-response' } : { status: 'ready', issued };
}

/** `POST /v1/admin/employees/{employee_id}/password-reset` — ไม่มี body; ปลดล็อก บังคับเปลี่ยนรหัส และตัดทุก session รวมแอป SSO */
export async function resetEmployeePassword(
  employeeId: string,
  options: RbacAdminRequestOptions = {},
): Promise<IssueLoginResult> {
  const result = await requestPayload(
    buildEmployeePasswordResetPath(employeeId),
    { method: 'POST', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const issued = parseIssuedLogin(result.payload);
  return issued === null ? { status: 'invalid-response' } : { status: 'ready', issued };
}

/** `email: null` = ล้างอีเมล */
export async function setEmployeeEmail(
  employeeId: string,
  email: string | null,
  options: RbacAdminRequestOptions = {},
): Promise<AckResult> {
  return ack(
    await requestPayload(
      buildEmployeePath(employeeId),
      { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ email }) },
      options,
    ),
  );
}
